import { MouseEvent, MutableRefObject, useCallback, useMemo, useState } from 'react';

import { GridApi } from '@mui/x-data-grid-premium';

import { isPeriodField } from '../timeline/timeline.utils';

export interface ContextMenuTarget {
  kind: 'column' | 'empty';
  field: string | null;
  rowId: string | null;
  rawValue: unknown;
  isGroupHeader: boolean;
}

export interface ContextMenuState {
  position: { x: number; y: number };
  target: ContextMenuTarget;
}

interface UseTableContextMenuParams {
  apiRef: MutableRefObject<GridApi>;
  rows: unknown[];
  getRowId: (row: unknown) => string | number;
  groupByField?: string | null;
  columnRawFieldMap?: Record<string, string | ((row: unknown) => unknown)>;
}

export interface UseTableContextMenuReturn {
  state: ContextMenuState | null;
  open: (event: MouseEvent<HTMLElement>) => void;
  close: () => void;
}

const isEditableTarget = (el: HTMLElement): boolean =>
  !!el.closest('input, textarea, [contenteditable="true"]');

export default function useTableContextMenu({
  apiRef,
  rows,
  getRowId,
  groupByField,
  columnRawFieldMap,
}: UseTableContextMenuParams): UseTableContextMenuReturn {
  const [state, setState] = useState<ContextMenuState | null>(null);

  const close = useCallback(() => setState(null), []);

  const resolveRawValue = useCallback(
    (field: string, row: Record<string, unknown>): unknown => {
      const mapped = columnRawFieldMap?.[field];
      if (typeof mapped === 'function') return mapped(row);
      return row[mapped ?? field];
    },
    [columnRawFieldMap],
  );

  const open = useCallback(
    (event: MouseEvent<HTMLElement>) => {
      const targetEl = event.target as HTMLElement;
      if (isEditableTarget(targetEl)) return;
      event.preventDefault();

      const position = { x: event.clientX, y: event.clientY };
      const cellEl = targetEl.closest('[data-field]') as HTMLElement | null;
      const cellField = cellEl?.getAttribute('data-field') ?? null;
      // A timeline period cell belongs to no page column, so it gets the table-level menu.
      const field = cellField && !isPeriodField(cellField) ? cellField : null;
      const rowEl = targetEl.closest('[data-id]');
      const rowId = rowEl?.getAttribute('data-id') ?? null;

      if (rowId) {
        const node = apiRef.current?.getRowNode?.(rowId);
        // Only the grouping's own header rows are auto-generated. A tree parent (a package above
        // its member spaces) is a group node too, but it is a real row with cells of its own.
        if (node?.type === 'group' && node.isAutoGenerated && groupByField) {
          setState({
            position,
            target: {
              kind: 'column',
              field: groupByField,
              rowId,
              rawValue: node.groupingKey,
              isGroupHeader: true,
            },
          });
          return;
        }
      }

      if (!field) {
        setState({
          position,
          target: {
            kind: 'empty',
            field: null,
            rowId: null,
            rawValue: undefined,
            isGroupHeader: false,
          },
        });
        return;
      }

      const row = rowId != null ? rows.find((r) => String(getRowId(r)) === rowId) : undefined;
      const rawValue = row ? resolveRawValue(field, row as Record<string, unknown>) : undefined;
      setState({
        position,
        target: { kind: 'column', field, rowId, rawValue, isGroupHeader: false },
      });
    },
    [apiRef, rows, getRowId, groupByField, resolveRawValue],
  );

  return useMemo(() => ({ state, open, close }), [state, open, close]);
}
